import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const MOBILE_BREAKPOINT = 768;
const SIDEBAR_WIDTH = 168;
const LAZY_ROOT_MARGIN = "600px 0px"; // 뷰포트 앞뒤로 이만큼 미리 렌더링
const DEFAULT_PAGE_RATIO = Math.SQRT2; // 로드되기 전까지만 쓰는 임시 자리 예약 비율

// numPages가 null(아직 모름)이면 사이드바 있다고 가정해서 나중에 나타날 때
// 레이아웃이 덜컹거리지 않게 하고, 정확히 1페이지로 확인되면 그때만 숨긴다.
const computeSizes = (numPages) => {
  const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
  const hasSidebar = !isMobile && numPages !== 1;
  const contentWidth = isMobile
    ? window.innerWidth - 32
    : Math.min(window.innerWidth - (hasSidebar ? SIDEBAR_WIDTH : 0) - 96, 1400);
  return { isMobile, hasSidebar, contentWidth };
};

// 페이지가 몇 백 장이 될지 모르니, 컨테이너(root) 뷰포트 근처에 들어온 페이지만
// 실제로 캔버스를 렌더링한다. 비율도 페이지 각자가 로드되는 순간 스스로 맞추기
// 때문에(전역으로 공유하지 않음), 한 페이지가 로드됐다고 다른 페이지들이
// 같이 리사이즈되며 스크롤이 튀는 일이 없다.
function LazyPage({ pageNumber, width, rootRef, className, onRef, onClick, children }) {
  const elRef = useRef(null);
  const [visible, setVisible] = useState(pageNumber === 1);
  const [ratio, setRatio] = useState(DEFAULT_PAGE_RATIO);

  useEffect(() => {
    if (visible) return;
    const el = elRef.current;
    const root = rootRef.current;
    if (!el || !root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setVisible(true);
      },
      { root, rootMargin: LAZY_ROOT_MARGIN }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, rootRef]);

  return (
    <div
      className={className}
      style={{ aspectRatio: `1 / ${ratio}` }}
      onClick={onClick}
      ref={(el) => {
        elRef.current = el;
        if (onRef) onRef(el);
      }}
    >
      {visible && (
        <Page
          pageNumber={pageNumber}
          width={width}
          onLoadSuccess={(page) => setRatio(page.originalHeight / page.originalWidth)}
          renderAnnotationLayer={false}
          renderTextLayer={false}
        />
      )}
      {children}
    </div>
  );
}

// 크롬/엣지 내장 pdf 뷰어처럼: 데스크탑엔 좌측에 페이지 썸네일(클릭하면 해당 페이지로 스크롤),
// 우측/모바일은 전체 페이지를 이어붙여서 그냥 스크롤로 보는 방식.
function PdfViewerButton({ url, label, style }) {
  const [open, setOpen] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [sizes, setSizes] = useState(computeSizes(null));
  const mainRefs = useRef({});
  const sidebarRef = useRef(null);
  const mainScrollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const handleResize = () => setSizes(computeSizes(numPages));
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, numPages]);

  const scrollToPage = (n) => {
    const node = mainRefs.current[n];
    if (node) node.scrollIntoView({ block: "start", behavior: "smooth" });
  };

  return (
    <>
      <button
        type="button"
        style={style}
        onClick={() => setOpen(true)}
      >
        {label}
      </button>

      {open &&
        createPortal(
          <div className="pdfModalOverlay">
            <div className="pdfModalTopbar">
              <span className="pdfModalTitle">{label}</span>
              <span className="pdfModalPageInfo">
                {numPages ? `총 ${numPages}페이지` : "..."}
              </span>
              <button
                type="button"
                className="pdfModalClose"
                onClick={() => setOpen(false)}
                aria-label="닫기"
              >
                &times;
              </button>
            </div>

            <div className="pdfModalBody">
              <Document
                file={url}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                loading="PDF 불러오는 중..."
                error="PDF를 불러오지 못했습니다."
              >
                {sizes.hasSidebar && numPages && (
                  <div className="pdfSidebar" ref={sidebarRef}>
                    {Array.from({ length: numPages }, (_, i) => i + 1).map(
                      (n) => (
                        <LazyPage
                          key={n}
                          pageNumber={n}
                          width={130}
                          rootRef={sidebarRef}
                          className="pdfThumb"
                          onClick={() => scrollToPage(n)}
                        >
                          <span className="pdfThumbNum">{n}</span>
                        </LazyPage>
                      )
                    )}
                  </div>
                )}

                <div className="pdfMainArea">
                  <div className="pdfMainScroll" ref={mainScrollRef}>
                    {Array.from(
                      { length: numPages || 0 },
                      (_, i) => i + 1
                    ).map((n) => (
                      <LazyPage
                        key={n}
                        pageNumber={n}
                        width={sizes.contentWidth}
                        rootRef={mainScrollRef}
                        className="pdfMainPage"
                        onRef={(el) => {
                          mainRefs.current[n] = el;
                        }}
                      />
                    ))}
                  </div>
                </div>
              </Document>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

export default PdfViewerButton;
